import {
    Icon,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableHeader,
    TableRow,
} from '@ksyos/design-system';
import { sortBy, uniqBy } from 'lodash';
import { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import type { IProcessQuestionnaireResponse } from '@common/interfaces/api/processQuestionnaireResponsesApi';
import type { IQuestionnaireItem, IQuestionnaireItemAnswerOption } from '@common/interfaces/api/questionnairesApi';
import { QuestionnaireItemAnswerType } from '@common/enums';

interface IObesiQTableProps {
    processQuestionnaireResponse: IProcessQuestionnaireResponse;
    questionnaireItems: IQuestionnaireItem[];
}

interface ITableBodyData {
    answerOptionsChecked: boolean[];
    text: string;
}

export function ObesiQTable(props: IObesiQTableProps): JSX.Element {
    const { processQuestionnaireResponse, questionnaireItems } = props;
    const [tableBodyData, setTableBodyData] = useState<ITableBodyData[]>([]);
    const answerOptions = getAnswerOptions(questionnaireItems);
    const filteredQuestionnaireItems = getFilteredQuestionnaireItems(questionnaireItems);

    useEffect(() => {
        loadTableBodyData();
    }, [processQuestionnaireResponse]);

    return (
        <TableContainer>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableHeader>Vraag</TableHeader>
                        {answerOptions.map((answerOption) => (
                            <TableHeader
                                align="center"
                                key={answerOption?.itemWeight}
                            >
                                {answerOption?.value}
                            </TableHeader>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {tableBodyData.map((item) => (
                        <TableRow key={uuidv4()}>
                            <TableCell>{item.text}</TableCell>
                            {item.answerOptionsChecked.map((isChecked) => (
                                <TableCell
                                    align="center"
                                    key={uuidv4()}
                                >
                                    {isChecked ? <Icon icon="closed" /> : null}
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );

    function getAnswerOptions(questionnaireItems: IQuestionnaireItem[]): IQuestionnaireItemAnswerOption[] {
        const answerOptions: IQuestionnaireItemAnswerOption[][] = [];
        for (const item of questionnaireItems) {
            if (typeof item.answerOptions !== 'undefined') {
                answerOptions.push(item.answerOptions);
            }
        }
        const flatAnswerOptions = answerOptions.flatMap((item) => item);
        const uniqueAnswerOptions = uniqBy(flatAnswerOptions, 'value');

        return sortBy(uniqueAnswerOptions, 'itemWeight');
    }

    function getFilteredQuestionnaireItems(questionnaireItems: IQuestionnaireItem[]): IQuestionnaireItem[] {
        const questions = questionnaireItems.filter(
            (question) => question.answerType === QuestionnaireItemAnswerType.options
        );

        return sortBy(questions, 'prefix');
    }

    function loadTableBodyData(): void {
        const tableBodyData: ITableBodyData[] = [];

        for (const questionnaireItem of filteredQuestionnaireItems) {
            const answerOptionsChecked: boolean[] = Array(answerOptions.length).fill(false);
            const responseItem = processQuestionnaireResponse?.items?.find(
                (item) => item.prefix === questionnaireItem.prefix
            );
            if (responseItem) {
                const answerOptionIndex = answerOptions.findIndex(
                    (item) =>
                        item.itemWeight === responseItem.answer.itemWeight && item.value === responseItem.answer.value
                );
                if (answerOptionIndex !== -1) {
                    answerOptionsChecked[answerOptionIndex] = true;
                }
            }
            tableBodyData.push({ answerOptionsChecked, text: questionnaireItem.text });
        }

        setTableBodyData(tableBodyData);
    }
}
